import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'framer-motion';
import { cn } from '@/lib/utils';

/*
 * Um jeito só de escolher arrastando, em toda a plataforma (30/10: "tudo tem
 * que funcionar desse mesmo jeito… todos os botões têm que ser possíveis de
 * arrastar", e "tenho que conseguir arrastar com um dedo também").
 *
 * Vale para a barra de baixo do celular, as abas (do aluno e do admin), o
 * filtro do ranking e as laterais. A lente (o realce do item escolhido):
 *
 *   - no clique ou toque, desliza até o item novo e assenta com um tico de mola;
 *   - segurando (dedo ou mouse) e arrastando, ela vai junto e TRAVA no item mais
 *     perto, nunca fica no meio de dois ("senão fica feio", 29/10). Ao soltar,
 *     o item onde travou é o escolhido;
 *   - no caminho ela estica: a borda da frente chega antes da de trás, como uma
 *     gota, e amassa um pouco na altura enquanto está esticada;
 *   - apertada, ela cresce um tico (sente que pegou).
 *
 * Toque: numa barra que cabe inteira, pega-se de qualquer ponto. Numa barra que
 * rola (abas do admin no celular), só o item escolhido pega a lente; arrastar
 * nos outros continua rolando a barra, e a lente perto da borda rola a barra
 * sozinha. O `touch-action` é posto só onde o arraste é nosso, para a página
 * continuar rolando no resto.
 */

export interface CaixaDaLente {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Opcoes {
  /** 'x' para barra deitada, 'y' para menu em pé. */
  eixo: 'x' | 'y';
  /** Seletor dos itens dentro do container. */
  seletor: string;
  /** Item escolhido agora: o índice, ou a chave (ver `chave`); -1/null = nenhum. */
  atual: number | string | null;
  /** Lê a chave de um item (ex.: a rota), para `atual` poder ser a chave. */
  chave?: (item: HTMLElement) => string;
  /** Soltou depois de arrastar: o item onde a lente travou. */
  aoEscolher: (indice: number, item: HTMLElement) => void;
  /**
   * 'qualquer': aperta em qualquer ponto e arrasta (barras e abas).
   * 'atual': só o item escolhido se arrasta; os outros seguem sendo links e o
   * menu continua rolando (laterais).
   */
  pega?: 'qualquer' | 'atual';
}

const IGUAL = (a: CaixaDaLente[], b: CaixaDaLente[]) =>
  a.length === b.length &&
  a.every(
    (c, i) =>
      c.x === b[i].x && c.y === b[i].y && c.w === b[i].w && c.h === b[i].h,
  );

/** Posição do item dentro do container, pelo layout (transform não engana). */
function caixaNoContainer(item: HTMLElement, base: HTMLElement): CaixaDaLente {
  let x = 0;
  let y = 0;
  let no: HTMLElement | null = item;
  while (no && no !== base) {
    x += no.offsetLeft;
    y += no.offsetTop;
    const pai = no.offsetParent as HTMLElement | null;
    if (pai !== base && (!pai || !base.contains(pai))) {
      // o container não é o offsetParent: cai para a medida da tela
      const a = item.getBoundingClientRect();
      const b = base.getBoundingClientRect();
      return {
        x: a.left - b.left - base.clientLeft + base.scrollLeft,
        y: a.top - b.top - base.clientTop + base.scrollTop,
        w: item.offsetWidth,
        h: item.offsetHeight,
      };
    }
    no = pai;
  }
  return { x, y, w: item.offsetWidth, h: item.offsetHeight };
}

export function useArrasteQueTrava<E extends HTMLElement>({
  eixo,
  seletor,
  atual,
  chave,
  aoEscolher,
  pega = 'qualquer',
}: Opcoes) {
  const ref = useRef<E>(null) as MutableRefObject<E | null>;
  const itens = useRef<HTMLElement[]>([]);
  const [caixas, setCaixas] = useState<CaixaDaLente[]>([]);
  const [chaves, setChaves] = useState<string[]>([]);
  const [rola, setRola] = useState(false);
  // onde a lente está travada enquanto o dedo/mouse está apertado
  const [alvo, setAlvo] = useState<number | null>(null);
  // depois de soltar, a lente fica no escolhido até a página trocar
  const [pendente, setPendente] = useState<number | null>(null);
  const [apertado, setApertado] = useState(false);
  const [arrastando, setArrastando] = useState(false);

  const gesto = useRef<{
    id: number;
    x: number;
    y: number;
    desloc: { x: number; y: number };
    arrastando: boolean;
  } | null>(null);
  const ponteiro = useRef({ x: 0, y: 0 });
  const quadro = useRef(0);
  const soltouArrastando = useRef(0);
  // o laço de rolagem lê sempre o valor de agora
  const vivo = useRef({ caixas, rola, alvo });
  vivo.current = { caixas, rola, alvo };

  const medir = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const lista = Array.from(el.querySelectorAll<HTMLElement>(seletor));
    itens.current = lista;
    const novas = lista.map((i) => caixaNoContainer(i, el));
    setCaixas((velhas) => (IGUAL(velhas, novas) ? velhas : novas));
    if (chave) {
      const ks = lista.map(chave);
      setChaves((velhas) =>
        velhas.length === ks.length && velhas.every((k, i) => k === ks[i])
          ? velhas
          : ks,
      );
    }
    setRola(
      eixo === 'x'
        ? el.scrollWidth > el.clientWidth + 1
        : el.scrollHeight > el.clientHeight + 1,
    );
  }, [eixo, seletor, chave]);

  useLayoutEffect(() => {
    medir();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    // os itens chegam do banco depois da primeira pintura
    const mo = new MutationObserver(medir);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [medir]);

  const indiceAtual =
    typeof atual === 'number'
      ? atual
      : atual === null
        ? -1
        : chaves.indexOf(atual);

  // a página trocou: a lente passa a seguir o item escolhido de verdade
  useEffect(() => setPendente(null), [indiceAtual]);
  useEffect(() => {
    if (pendente === null) return;
    // link externo ou clique que não trocou nada: volta depois de um tempo
    const t = window.setTimeout(() => setPendente(null), 1400);
    return () => window.clearTimeout(t);
  }, [pendente]);

  const lenteEm = alvo ?? pendente ?? (indiceAtual >= 0 ? indiceAtual : null);
  const pegaSoAtual = (toque: boolean) => pega === 'atual' || (toque && rola);

  // touch-action só onde o arraste é nosso
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const livre = eixo === 'x' ? 'pan-y' : 'pan-x';
    const tudo = !pegaSoAtual(true);
    el.style.touchAction = tudo ? livre : '';
    const pegavel = pendente ?? indiceAtual;
    itens.current.forEach((item, i) => {
      item.style.touchAction = !tudo && i === pegavel ? livre : '';
    });
  });

  // numa barra que rola, o item escolhido não fica escondido
  useEffect(() => {
    const el = ref.current;
    const c = caixas[indiceAtual];
    if (!el || !c || !rola || gesto.current) return;
    if (eixo === 'x') {
      const ini = el.scrollLeft;
      const fim = ini + el.clientWidth;
      if (c.x < ini + 8 || c.x + c.w > fim - 8)
        el.scrollTo({
          left: c.x - (el.clientWidth - c.w) / 2,
          behavior: 'smooth',
        });
    } else {
      const ini = el.scrollTop;
      const fim = ini + el.clientHeight;
      if (c.y < ini + 8 || c.y + c.h > fim - 8)
        el.scrollTo({
          top: c.y - (el.clientHeight - c.h) / 2,
          behavior: 'smooth',
        });
    }
    // só quando o escolhido muda
  }, [indiceAtual]);

  const noContainer = (cx: number, cy: number) => {
    const el = ref.current!;
    const r = el.getBoundingClientRect();
    return {
      x: cx - r.left - el.clientLeft + el.scrollLeft,
      y: cy - r.top - el.clientTop + el.scrollTop,
    };
  };

  /** Item com o centro mais perto do ponto (a troca acontece na metade). */
  const maisPerto = (p: { x: number; y: number }) => {
    let melhor = 0;
    let menor = Infinity;
    vivo.current.caixas.forEach((c, i) => {
      const d = Math.hypot(c.x + c.w / 2 - p.x, c.y + c.h / 2 - p.y);
      if (d < menor) {
        menor = d;
        melhor = i;
      }
    });
    return melhor;
  };

  const travar = () => {
    const g = gesto.current;
    if (!g) return;
    const p = noContainer(ponteiro.current.x, ponteiro.current.y);
    const i = maisPerto({ x: p.x - g.desloc.x, y: p.y - g.desloc.y });
    if (i !== vivo.current.alvo) setAlvo(i);
  };

  /** Lente encostada na borda de uma barra que rola: a barra anda sozinha. */
  const rolarNaBorda = () => {
    cancelAnimationFrame(quadro.current);
    const passo = () => {
      const g = gesto.current;
      const el = ref.current;
      if (!g?.arrastando || !el) return;
      if (vivo.current.rola) {
        const r = el.getBoundingClientRect();
        const pos =
          eixo === 'x'
            ? ponteiro.current.x - r.left
            : ponteiro.current.y - r.top;
        const tam = eixo === 'x' ? r.width : r.height;
        const borda = Math.min(48, tam / 4);
        let v = 0;
        if (pos < borda) v = -((borda - pos) / borda) * 14;
        else if (pos > tam - borda) v = ((pos - (tam - borda)) / borda) * 14;
        if (v) {
          if (eixo === 'x') el.scrollLeft += v;
          else el.scrollTop += v;
          travar();
        }
      }
      quadro.current = requestAnimationFrame(passo);
    };
    quadro.current = requestAnimationFrame(passo);
  };

  const encerrar = () => {
    gesto.current = null;
    cancelAnimationFrame(quadro.current);
    setApertado(false);
    setArrastando(false);
    setAlvo(null);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!vivo.current.caixas.length || gesto.current) return;
    const item = (e.target as Element).closest<HTMLElement>(seletor);
    const indiceItem = item ? itens.current.indexOf(item) : -1;
    const soAtual = pegaSoAtual(e.pointerType !== 'mouse');
    const pegavel = pendente ?? indiceAtual;
    if (soAtual && (indiceItem < 0 || indiceItem !== pegavel)) return;
    const p = noContainer(e.clientX, e.clientY);
    const de = indiceItem >= 0 ? indiceItem : maisPerto(p);
    const c = vivo.current.caixas[de];
    gesto.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      // onde o item foi pego: sem isso a lente pularia para o centro do dedo
      desloc:
        soAtual && c
          ? { x: p.x - (c.x + c.w / 2), y: p.y - (c.y + c.h / 2) }
          : { x: 0, y: 0 },
      arrastando: false,
    };
    ponteiro.current = { x: e.clientX, y: e.clientY };
    setApertado(true);
    // de qualquer ponto: a lente já vai para baixo do dedo no aperto
    if (!soAtual) setAlvo(de);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesto.current;
    if (!g || e.pointerId !== g.id) return;
    ponteiro.current = { x: e.clientX, y: e.clientY };
    if (!g.arrastando) {
      // um tremidinho no clique não vira arraste
      const d =
        eixo === 'x' ? Math.abs(e.clientX - g.x) : Math.abs(e.clientY - g.y);
      if (d < 6) return;
      g.arrastando = true;
      setArrastando(true);
      try {
        ref.current?.setPointerCapture(g.id);
      } catch {
        /* o ponteiro já foi embora */
      }
      rolarNaBorda();
    }
    travar();
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const g = gesto.current;
    if (!g || e.pointerId !== g.id) return;
    const escolhido = vivo.current.alvo;
    encerrar();
    if (escolhido !== null) setPendente(escolhido);
    if (g.arrastando && escolhido !== null && itens.current[escolhido]) {
      soltouArrastando.current = performance.now();
      aoEscolher(escolhido, itens.current[escolhido]);
    }
  };

  // depois de um arraste o navegador ainda manda um clique: não vale
  const onClickCapture = (e: React.MouseEvent) => {
    if (performance.now() - soltouArrastando.current < 400) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return {
    ref,
    props: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: encerrar,
      onClickCapture,
    },
    /** Caixa onde a lente deve estar agora (null = nenhum item). */
    caixa: lenteEm !== null ? (caixas[lenteEm] ?? null) : null,
    /** Índice do item embaixo da lente (o que aparece aceso). */
    lente: lenteEm,
    chaves,
    /** Acende um item na hora, antes de a página nova abrir (clique). */
    acender: (indice: number) => setPendente(indice),
    apertado,
    arrastando,
  };
}

interface LenteProps {
  caixa: CaixaDaLente | null;
  eixo?: 'x' | 'y';
  arrastando?: boolean;
  apertada?: boolean;
  className?: string;
}

/** O realce que desliza, estica no caminho e trava no item. */
export function Lente({
  caixa,
  eixo = 'x',
  arrastando = false,
  apertada = false,
  className,
}: LenteProps) {
  const quieto = useReducedMotion();
  const ini = useMotionValue(0);
  const fim = useMotionValue(0);
  const cruzIni = useMotionValue(0);
  const cruzTam = useMotionValue(0);
  const tamAlvo = useRef(0);
  const posta = useRef(false);

  useLayoutEffect(() => {
    if (!caixa) {
      // some no lugar; ao voltar, nasce no item novo em vez de atravessar a barra
      posta.current = false;
      return;
    }
    const [a, b, c, d] =
      eixo === 'x'
        ? [caixa.x, caixa.x + caixa.w, caixa.y, caixa.h]
        : [caixa.y, caixa.y + caixa.h, caixa.x, caixa.w];
    tamAlvo.current = b - a;
    if (!posta.current || quieto) {
      ini.set(a);
      fim.set(b);
      cruzIni.set(c);
      cruzTam.set(d);
      posta.current = true;
      return;
    }
    const paraFrente = a > ini.get() || b > fim.get();
    // a borda da frente puxa, a de trás vem depois: a lente estica e volta
    const frente = arrastando
      ? { stiffness: 1100, damping: 46, mass: 0.5 }
      : { stiffness: 560, damping: 30, mass: 0.8 };
    const tras = arrastando
      ? { stiffness: 520, damping: 34, mass: 0.5 }
      : { stiffness: 230, damping: 20, mass: 0.9 };
    animate(ini, a, { type: 'spring', ...(paraFrente ? tras : frente) });
    animate(fim, b, { type: 'spring', ...(paraFrente ? frente : tras) });
    animate(cruzIni, c, { type: 'spring', ...frente });
    animate(cruzTam, d, { type: 'spring', ...frente });
  }, [caixa?.x, caixa?.y, caixa?.w, caixa?.h, eixo, arrastando, quieto]);

  const tam = useTransform([ini, fim], ([a, b]: number[]) =>
    Math.max(0, b - a),
  );
  // esticada, amassa um pouco no outro eixo (até 14%)
  const amassa = useTransform([ini, fim], ([a, b]: number[]) => {
    const t = tamAlvo.current;
    const sobra = t > 0 ? Math.max(0, (b - a) / t - 1) : 0;
    return 1 - Math.min(sobra, 1) * 0.14;
  });

  const estilo =
    eixo === 'x'
      ? { x: ini, width: tam, y: cruzIni, height: cruzTam, scaleY: amassa }
      : { y: ini, height: tam, x: cruzIni, width: cruzTam, scaleX: amassa };

  return (
    <motion.div
      aria-hidden="true"
      className={cn('pointer-events-none absolute left-0 top-0', className)}
      style={estilo}
      initial={false}
      animate={{
        opacity: caixa ? 1 : 0,
        scale: !caixa ? 0.85 : apertada ? 1.06 : 1,
      }}
      transition={
        quieto
          ? { duration: 0 }
          : { type: 'spring', stiffness: 520, damping: 26, mass: 0.7 }
      }
    />
  );
}
